import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// globals.css is imported (into a cascade layer) by tailwind.css so Tailwind
// utilities correctly win over the PWA's unlayered element styles.
import "./styles/tailwind.css";
import "./styles/ato-ui.css";
import App from "./App";
import PwaUpdateGate from "./components/PwaUpdateGate";
import StaleBuildNotice from "./components/StaleBuildNotice";
import { installChunkLoadRecovery } from "./app/staleBuild";
import { installApiFetchCompatibility } from "./app/clientContract";
import { installStartupHttpTiming } from "./performance/httpTiming";
import { preconnectApiHost } from "@/components/runs/networkHints";
import { startAnalytics } from "./analytics/client";
import { initializeLocale } from "./i18n/locale";
import { prefetchAccountSession } from "./auth/accountSession";
import { adoptEmbeddedDiscoverPage, prefetchDiscoverPage, type DiscoverPage } from "./components/personal-apps/discoverClient";
import { connectionProfile } from "./app/connection/resolveProfile";
import { matchesWideScreen } from "./components/personal-apps/discovery/EditorialSlots";

// Build-time distribution constant. Vite replaces `import.meta.env.*` with a
// literal, so in the Desktop build the branches below fold to `false` and both
// the fetch layer and the update gate are dropped from the bundle — the same
// "compiled out, not skipped at runtime" rule the Service Worker registration
// already follows.
const isHostedPwa = import.meta.env.VITE_ATO_DISTRIBUTION !== "desktop";

// Install the PWA/API compatibility fetch layer BEFORE anything can issue an
// API request: it stamps every account-API call with this build's contract and
// absorbs a compatibility `426 Upgrade Required` into the blocking update gate.
// Only the API origin is affected, and it is a HOSTED PWA concern only — the
// bundled Desktop app cannot be updated by a web deploy.
if (isHostedPwa) {
  installApiFetchCompatibility();
  installStartupHttpTiming();
  // A chunk from an earlier deploy that can no longer be loaded: offer a reload.
  installChunkLoadRecovery();
}

/** DiscoveryWorkspace's first read; the Worker embeds the same query. */
const HOME_FIRST_PAGE = { limit: 12, sort: "recommended" } as const;

/** The signed-out first page the Worker put in the HTML, if any. */
function embeddedFirstPage(): DiscoverPage | null {
  const text = document.getElementById("ato-discover-first-page")?.textContent;
  if (!text) return null;
  try {
    const page = JSON.parse(text) as DiscoverPage;
    return Array.isArray(page?.apps) ? page : null;
  } catch {
    return null;
  }
}

// Start the first screen's reads now, while the dictionary loads and before
// React mounts: the session and the Discover home's first page run side by
// side instead of the page waiting for the session's round trip.
if (isHostedPwa) {
  prefetchAccountSession();
  if (isPlainDiscoverHome(window.location)) {
    const embedded = embeddedFirstPage();
    // A bearer connection does not use the cookie the server looked for.
    if (embedded && connectionProfile().auth.kind !== "bearer") adoptEmbeddedDiscoverPage(HOME_FIRST_PAGE, embedded);
    else prefetchDiscoverPage(HOME_FIRST_PAGE);
    if (matchesWideScreen()) prefetchDiscoverPage({ sort: "popular", limit: 24 });
  }
}

/** `/` with no state that changes the first page's query. */
function isPlainDiscoverHome(location: Location): boolean {
  if (location.pathname !== "/") return false;
  const params = new URLSearchParams(location.search);
  return [...params.keys()].every((key) => key === "hero" || (key === "view" && params.get("view") === "discover"));
}

// Hosted HTML already carries this hint. Keep the runtime resolver for Desktop
// injection and development documents; it reuses an existing matching link.
preconnectApiHost();

// Analytics. The launch funnel, capsule views, searches, snapshots, publish
// requests and session length are all recorded by the SERVER from the requests
// that carry them — the browser no longer reports any of it, and the legacy
// runFunnel sink is console-only again. What is left here is the page-level
// event the server cannot yet see.
startAnalytics();

// Service Worker registration is a HOSTED distribution concern, not a feature.
//
// It is compiled out of the Desktop build entirely rather than skipped at
// runtime: `import.meta.env.VITE_ATO_DISTRIBUTION` is a build-time constant, so
// Vite eliminates this whole branch and the desktop bundle contains no
// registration code at all. "Not present" is a much stronger guarantee than
// "present but not reached", and this specific mechanism has already burned us
// — a stale `ato-pwa-shell-v7` cache served a retired shell on staging long
// after it was deleted, which is exactly the failure a bundled desktop app must
// never be able to reproduce.
if (isHostedPwa) {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).catch(() => {
        // SW registration failure is non-fatal
      });
    });
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("No #root element");
void initializeLocale().then(() => {
  createRoot(root).render(
    <StrictMode>
      <App />
      {isHostedPwa && <PwaUpdateGate />}
      {isHostedPwa && <StaleBuildNotice />}
    </StrictMode>
  );
  // index.html hides the boot skeleton with CSS once #root holds the app;
  // removing it after the first commit covers browsers without :has().
  requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById("boot-skeleton")?.remove()));
});
